import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { toast } from "react-toastify";
import type { FacilityHierarchyInput } from "@utils/facilityUtils";
import { getFacilityHierarchyId } from "@utils/facilityUtils";
import { createUnlinkRequest } from "@services/facilityNetworkUnlink";
import FacilityNetworkFigmaCard from "./FacilityNetworkFigmaCard";
import FacilityNetworkLeaveNetworkDialog from "./FacilityNetworkLeaveNetworkDialog";
import { parentFacilityMetaLine } from "./facilityNetworkOverviewUtils";
import { FN } from "./facilityNetworkTheme";

type Props = {
  parent: FacilityHierarchyInput;
  childFacilityId: number | null;
  onViewParentDetails: () => void;
  onNetworkMutated?: () => void;
};

export default function FacilityNetworkParentFacilityCard({
  parent,
  childFacilityId,
  onViewParentDetails,
  onNetworkMutated,
}: Props) {
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const parentId = getFacilityHierarchyId(parent);

  const handleLeave = async (reason?: string) => {
    if (childFacilityId == null || parentId == null) {
      toast.error("Unable to determine facilities for this unlink request.");
      return;
    }
    setSubmitting(true);
    try {
      await createUnlinkRequest(childFacilityId, {
        counterpartyFacilityId: parentId,
        reason: reason?.trim() || "Child requested to leave the facility network",
      });
      toast.success("Unlink request sent. The parent facility must approve it.");
      setLeaveOpen(false);
      onNetworkMutated?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send unlink request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <FacilityNetworkFigmaCard>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: { xs: "wrap", md: "nowrap" },
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 12,
                fontWeight: 600,
                color: FN.primary,
                letterSpacing: "0.25px",
                lineHeight: "16px",
              }}
            >
              Parent Facility
            </Typography>
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 16,
                fontWeight: 600,
                color: FN.figmaNeutral,
                lineHeight: "22px",
                mt: 0.5,
              }}
            >
              {parent.facility_name?.trim() || "Parent facility"}
            </Typography>
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 12,
                color: FN.figmaNeutral,
                letterSpacing: "0.25px",
                lineHeight: "16px",
                mt: 0.5,
              }}
            >
              {parentFacilityMetaLine(parent)}
            </Typography>
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 12,
                color: FN.figmaAccent,
                letterSpacing: "0.25px",
                lineHeight: "16px",
                mt: 0.5,
              }}
            >
              Linked in this network · initiated by parent
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
            {/* <Button
              variant="outlined"
              onClick={onViewParentDetails}
              sx={{
                textTransform: "none",
                height: 32,
                px: 2,
                borderRadius: "4px",
                borderColor: "#d4d4d4",
                color: FN.text,
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 600,
                boxShadow: "0px 2px 4px rgba(0,0,0,0.15)",
                bgcolor: "#fff",
                "&:hover": { borderColor: FN.muted, bgcolor: "#fff" },
              }}
            >
              View Parent Details
            </Button> */}
            <Button
              variant="outlined"
              onClick={() => setLeaveOpen(true)}
              sx={{
                textTransform: "none",
                height: 32,
                px: 1.5,
                borderRadius: "4px",
                borderColor: FN.figmaErrorStroke,
                color: FN.figmaError,
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 600,
                bgcolor: "#fff",
                "&:hover": { borderColor: FN.figmaError, bgcolor: "#fff8f8" },
              }}
            >
              Leave Network
            </Button>
          </Box>
        </Box>
      </FacilityNetworkFigmaCard>
      <FacilityNetworkLeaveNetworkDialog
        open={leaveOpen}
        parentName={parent.facility_name?.trim() || "parent facility"}
        facilityRole="parent"
        submitting={submitting}
        onClose={() => {
          if (!submitting) setLeaveOpen(false);
        }}
        onConfirm={(reason) => {
          void handleLeave(reason);
        }}
      />
    </>
  );
}
